// import { TopbarButton } from "@/ui/Layout/Topbar/Topbar";
import { ButtonTypes, Variants } from "@/ui/types";
import { ArtifactOptions } from "../types";
import Button from "@/ui/Button";
import { getSessionToken } from "@/api/auth";
import {
  cloneCampaign,
  createCampaignAndObject,
  createObject,
  updateCampaign,
} from "@/api/campaigns";
import { useNavigate, useLocation } from "react-router-dom";
import { createFunnelObjects } from "@/Apps/Sketchpad/Sketchpad";
import { createPage } from "@/api/pages";
import slugify from "slugify";
import React, { useContext } from "react";
import { AppContext } from "@/AppContext";
import { saveOnboardingData } from "@/api/entities";
import { AgentMode } from "../AgentModeButton";
import { EditorTypes } from "@/Apps/Pages/Editor/EditorCanvas";
import { parseLiquidObjectWithLiquidJS } from "@/Apps/Pages/Page/LiquidRenderer";
import { unmapCampaignVariables } from "@/Apps/Pages/utils";
import { Topbar, TopbarButton } from "@/ui/Layout/Topbar/Topbar";
import { TopbarProps } from "@/ui/Layout/Topbar/types";
import { PaintBrush } from "@phosphor-icons/react";

interface ArtifactHeaderProps {
  artifactOptions: ArtifactOptions;
  setArtifactOptions: (options: ArtifactOptions) => void;
  hasChildren: boolean;
  mode?: AgentMode;
  topBarSettings?: TopbarProps;
  thread: any;
  onClose?: () => void;
}

const CloseButton = ({
  onClose,
}: {
  onClose: () => void;
}) => {
  return (
    <button
      onClick={onClose}
      className="p-2 rounded-full hover:bg-black/5 dark:hover:bg-white/5"
      aria-label="Close"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <line x1="18" y1="6" x2="6" y2="18"></line>
        <line x1="6" y1="6" x2="18" y2="18"></line>
      </svg>
    </button>
  );
};

export const ArtifactHeader = ({
  artifactOptions,
  setArtifactOptions,
  hasChildren,
  mode,
  topBarSettings = {},
  thread,
  onClose = () => {},
}: ArtifactHeaderProps) => {
  const {
    type,
    content = [],
    pageContent = [],
    campaignContent = [],
    title,
  } = artifactOptions?.container || {};
  const navigate = useNavigate();
  const { setCopilotContext, copilotContext } = useContext(AppContext);
  const { currentlyViewing } = useContext(AppContext);

  const handleSaveInLaunchContent = async () => {
    const contentName = window.prompt("Enter a name for the content");
    if (!contentName) return;

    let useContent = content?.[0]?.content;

    console.log(
      "ARTIFACT CONTAINER: handleSmartContentOpen",
      contentName,
      useContent
    );
    const token = getSessionToken();
    // need to handle conditionally (what if it already exists?)
    const { campaign, object } = await createCampaignAndObject({
      token,
      name: contentName,
      type: "content",
      nodeType: "ContentComponent",
      settings: {
        name: contentName,
        content: useContent,
        type: "Content Post",
      },
    });

    const objectId = object?.data?.createObject?.id;
    console.log(
      "ARTIFACT CONTAINER: createCampaignAndObject response",
      campaign,
      object,
      objectId
    );

    const href = `/content/${campaign?.id}/${objectId}`;
    console.log("ARTIFACT CONTAINER: href", href);

    navigate(href);
  };

  const handleSaveInLaunchFunnels = async () => {
    const funnelName = window.prompt("Enter a name for the funnel");
    if (!funnelName) return;

    console.log(
      "ARTIFACT CONTAINER: handleSaveInLaunchFunnels",
      funnelName,
      campaignContent
    );
    const token = getSessionToken();
    const blankCampaignId = "cjyt9x3v0001g0792aync7yje"; // The ID of the collection campaign template (blank)

    // 1. Create a new campaign (clone a blank one)
    const response = await cloneCampaign(blankCampaignId, {
      token,
      name: funnelName,
    });
    console.log("ARTIFACT CONTAINER: cloneCampaign response", response);
    const newCampaignId = response?.data?.cloneCampaign?.response?.id;

    // 2. Update the campaign with it
    const responseToCreateFunnelObjects = await createFunnelObjects(
      campaignContent,
      {
        id: newCampaignId,
      }
    );
    console.log(
      "ARTIFACT CONTAINER: createFunnelObjects",
      responseToCreateFunnelObjects
    );

    // 5. Navigate to the new campaign
    navigate(`/funnels/${newCampaignId}`);
  };

  const handleSaveInLaunchPages = async () => {
    const pageName = window.prompt("Enter a name for the page");
    if (!pageName) return;

    console.log(
      "ARTIFACT CONTAINER: handleSaveInLaunchPages",
      title,
      pageContent
    );

    const token = getSessionToken();

    const { campaign, object } = await createCampaignAndObject({
      token,
      name: pageName,
      type: "page",
      nodeType: "PageComponent",
      settings: { withEditorMode: EditorTypes.FULL },
    });

    const objectId = object?.data?.createObject?.id;
    console.log("ARTIFACT CONTAINER: Object Created", objectId, object);

    const parsedPageContent = parseLiquidObjectWithLiquidJS(pageContent, {
      activeContent: pageContent,
      campaignSettings: {
        variables: unmapCampaignVariables(
          artifactOptions.container?.payload?.pageSpec || {}
        ),
      },
      parseLiquid: true,
    });

    const slug = slugify(pageName, { lower: true, trim: true });
    const pgObj = {
      token,
      settings: {
        name: pageName,
        // content, //[bodyObj, ...content],
        content: parsedPageContent,
        slug,
        // campaignId: chatId,
        objectId,
        theme: "blank",
        type: "blank",
      },
    };

    console.log("ARTIFACT CONTAINER: Page Data (to save)", pgObj);
    const pageData = await createPage(pgObj);

    console.log("ARTIFACT CONTAINER: Page Created", pageData);
    navigate(`/pages/${campaign?.id}`);
  };

  const handleApplyToCurrentPage = async () => {
    console.log("ARTIFACT CONTAINER: handleApplyToCurrentPage");
    setArtifactOptions({
      ...artifactOptions,
      container: undefined,
      isShowing: hasChildren,
      newPageContent: pageContent,
    });
  };

  const handleApplyToCurrentCampaign = async () => {
    console.log("ARTIFACT CONTAINER: handleApplyToCurrentCampaign");
  };

  const handleSaveAsPlannerProfile = async () => {
    const profileName = window.prompt("Enter a name for the profile");
    if (!profileName) return;

    let useContent = content?.[0]?.content;
    console.log(
      "ARTIFACT CONTAINER: handleSaveAsPlannerProfile",
      profileName,
      useContent
    );

    const token = getSessionToken();
    const campaignId = "cjz0ij1qz0ems0792n1kpu5ac"; // id of the "blank" campaign template
    const response = await cloneCampaign(campaignId, {
      token,
      name: profileName,
    });

    const newCampaignId = response?.data?.cloneCampaign?.response?.id;

    // update the type of the campaign to be a tracking campaign
    await updateCampaign(newCampaignId, {
      token,
      settings: {
        type: "planning",
        settings: {
          type: "research",
        },
      },
    });

    await saveOnboardingData(
      token,
      { type: profileName, content: useContent },
      newCampaignId
    );

    console.log(
      "ARTIFACT CONTAINER: Onboarding Data Saved",
      { type: profileName, content: useContent },
      newCampaignId
    );

    // navigate(`/planner/${newCampaignId}`);
    alert(`Planner profile created: ${profileName}`);
  };

  // const handleCreateCampaignUsingThis = async () => {
  //   console.log("ARTIFACT CONTAINER: handleCreateCampaignUsingThis");
  //   setCopilotContext((prevContext: any) => ({
  //     ...prevContext,
  //     query: "Please create a marketing campaign around this document",
  //   }));
  // };

  // const handleCreatePageUsingThis = async () => {
  //   console.log("ARTIFACT CONTAINER: handleCreatePageUsingThis");
  //   setCopilotContext((prevContext: any) => ({
  //     ...prevContext,
  //     query: "Please create a page using this",
  //   }));
  // };

  // const handleCreateCopyUsingThis = async () => {
  //   console.log("ARTIFACT CONTAINER: handleCreateCopyUsingThis");
  //   setCopilotContext((prevContext: any) => ({
  //     ...prevContext,
  //     query: "Please create marketing copy around this document",
  //   }));
  // };

  const location = useLocation();

  if (type === "page_builder") {

    const showApplyRejectButtons = hasChildren && (location.pathname.includes("/pages/") || location.pathname.includes("/copilot"));

    const items = (
      <>
        {showApplyRejectButtons ? (
          <>
            <Button
              type={ButtonTypes.OUTLINED}
              variant={Variants.PRIMARY}
              label="Reject this change"
              onClick={() => {
                setArtifactOptions({
                  ...artifactOptions,
                  container: undefined,
                  isShowing: hasChildren,
                });
              }}
              // disabled={thread.isLoading}
            />
            <Button
              type={ButtonTypes.DEFAULT}
              variant={Variants.WARNING}
              label="Apply to Current Page"
              onClick={handleApplyToCurrentPage}
              // disabled={thread.isLoading}
            />
          </>
        ) : (
          <Button
            type={ButtonTypes.OUTLINED}
            variant={Variants.INFO}
            label="Save to Launch Pages"
            onClick={handleSaveInLaunchPages}
            // disabled={thread.isLoading}
          />
        )}
        {topBarSettings?.items}
        {!showApplyRejectButtons && <CloseButton onClose={onClose} />}
      </>
    );

    return (
      <div
        className={`fixed top-0 left-0 right-0 z-10 h-16 backdrop-blur animate-throb`}
      >
        {/* {JSON.stringify(location)} */}
        <Topbar
          hideSidebarToggleButton
          {...topBarSettings}
          items={items}
          hideCopilotButton={!hasChildren}
          className={!hasChildren ? "px-2" : "px-5"}
        />
      </div>
    );
  }

  if (type === "campaign_builder") {

    const items = (
      <>
        {hasChildren &&
        (location.pathname.includes("/funnels/") ||
          location.pathname.includes("/copilot")) ? (
          <Button
            type={ButtonTypes.OUTLINED}
            variant={Variants.INFO}
            label="Apply"
            onClick={handleApplyToCurrentCampaign}
          />
        ) : (
          <Button
            type={ButtonTypes.OUTLINED}
            variant={Variants.INFO}
            label="Save to Launch Funnels"
            onClick={handleSaveInLaunchFunnels}
          />
        )}
        {topBarSettings?.items}
        <CloseButton
          onClose={onClose}
        />
      </>
    );

    return (
      <div
        className={`fixed top-0 left-0 right-0 z-10 h-16 backdrop-blur animate-throb`}
      >
        <Topbar
          hideSidebarToggleButton
          {...topBarSettings}
          items={items}
          hideCopilotButton={!hasChildren}
          className={!hasChildren ? "px-2" : "px-5"}
        />
      </div>
    );
  }

  if (type === "content_writer") {

    const items = (      
      <>
        {topBarSettings?.items}
        <CloseButton
          onClose={onClose}
        />
      </>
    );

    return (
      <div
        className={`fixed top-0 left-0 right-0 z-10 h-16 backdrop-blur animate-throb`}
      >
        <Topbar
          hideSidebarToggleButton
          {...topBarSettings}
          items={items}
          hideCopilotButton={!hasChildren}
          className={!hasChildren ? "px-2" : "px-5"}
        />
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundColor: hasChildren ? "rgba(49, 46, 129, 0.5)" : "transparent",
      }}
      className={`flex justify-between items-center p-4 px-5 ${hasChildren && "bg-indigo-900/5 backdrop-blur"} border-b border-black/5 dark:border-white/5`}
    >
      <h2 className="text-lg font-semibold">{title || "Artifact"}</h2>
      <div className="flex items-center gap-2">
        {/*Deep Research Agent*/}
        {type === "deep_research" ||
          (mode === "Research" && (
            <>
              <Button
                type={ButtonTypes.ROUNDED}
                variant={Variants.BLACK}
                label="Save to Launch Planner"
                onClick={handleSaveAsPlannerProfile}
              />
              {/* <Button type={ButtonTypes.ROUNDED} variant={Variants.BLACK} label="Create Campaign using this" onClick={handleCreateCampaignUsingThis} /> */}
              {/* <Button type={ButtonTypes.ROUNDED} variant={Variants.BLACK} label="Create Page using this" onClick={handleCreatePageUsingThis} /> */}
              {/* <Button type={ButtonTypes.ROUNDED} variant={Variants.BLACK} label="Create Copy using this" onClick={handleCreateCopyUsingThis} /> */}
            </>
          ))}

        {/*Copywriting Agent*/}
        {type === "content_writer" && (
          <>
            {/* <Button type={ButtonTypes.ROUNDED} variant={Variants.BLACK} label="Create landing page around this" /> */}
            <Button
              type={ButtonTypes.ROUNDED}
              variant={Variants.BLACK}
              label="Save to Launch Content"
              onClick={handleSaveInLaunchContent}
            />
          </>
        )}

        {/*Campaign Building Agent*/}
        {type === "campaign_builder" && (
          <>
            {!hasChildren && (
              <Button
                type={ButtonTypes.ROUNDED}
                variant={Variants.BLACK}
                label="Save to Launch Funnels"
                onClick={handleSaveInLaunchFunnels}
              />
            )}
            {hasChildren && (
              <Button
                type={ButtonTypes.ROUNDED}
                variant={Variants.BLACK}
                label="Apply"
                onClick={handleApplyToCurrentCampaign}
              />
            )}
          </>
        )}

        {!hasChildren && (
          <CloseButton
            onClose={onClose}
          />
        )}
      </div>
    </div>
  );
};
